/**
 * Projects a reordered actionable sequence onto the complete encounter list.
 *
 * Returns null when actionable persisted IDs do not exactly match the
 * actionable IDs in `fullItems`, so callers never persist an incomplete list.
 * Hidden (non-actionable) items keep their relative order and slot positions.
 * Every id from `fullItems` appears exactly once when the merge succeeds.
 */
export function mergeActionableReorderIntoFullList<T extends { id: number }>(
  fullItems: readonly T[],
  actionableOrdered: readonly T[],
  isActionable: (item: T) => boolean,
): T[] | null {
  const fullActionableIds = fullItems
    .filter(isActionable)
    .map((item) => item.id);
  const orderedIds = actionableOrdered.map((item) => item.id);

  if (fullActionableIds.length !== orderedIds.length) return null;

  const orderedIdSet = new Set(orderedIds);
  if (orderedIdSet.size !== orderedIds.length) return null;
  if (fullActionableIds.some((id) => !orderedIdSet.has(id))) return null;

  const actionableById = new Map(
    actionableOrdered.map((item) => [item.id, item]),
  );
  let nextActionableIndex = 0;

  return fullItems.map((item) => {
    if (!isActionable(item)) {
      return item;
    }

    const replacement = actionableOrdered[nextActionableIndex];
    nextActionableIndex += 1;
    if (!replacement) {
      return actionableById.get(item.id) ?? item;
    }
    return actionableById.get(replacement.id) ?? replacement;
  });
}
